import { ShareableEntity } from "@/lib/api/shareable-entities";
import { ACTIVITY_REGISTRY } from "@/lib/activities/registry";
import { useEffect, useRef } from "react";
import { hubViewForEntity } from "./hub-view-for-entity";
import type { HubView } from "./use-hub-store";

interface UseHubEntityNavigationParams {
  entities: ShareableEntity[];
  selectedEntity: ShareableEntity | null;
  selectedNoteId: string | null;
  currentView: HubView;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  navigateToView: (
    view: HubView,
    entityId?: string | null,
    index?: number | null,
  ) => void;
}

const VIEWING_VIEWS: HubView[] = [
  "view-note",
  "view-moment",
  "view-swap",
  ...Object.values(ACTIVITY_REGISTRY).map((m) => m.hubView),
];

export function useHubEntityNavigation({
  entities,
  selectedEntity,
  selectedNoteId,
  currentView,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  navigateToView,
}: UseHubEntityNavigationParams) {
  const pendingNextNavigation = useRef<number | null>(null);

  useEffect(() => {
    if (
      VIEWING_VIEWS.includes(currentView) &&
      pendingNextNavigation.current !== null &&
      !isFetchingNextPage &&
      entities.length > pendingNextNavigation.current
    ) {
      const nextIndex = pendingNextNavigation.current;
      const nextEntity = entities[nextIndex];
      if (nextEntity) {
        navigateToView(hubViewForEntity(nextEntity), nextEntity.id);
        pendingNextNavigation.current = null;
      }
    }
  }, [entities, isFetchingNextPage, currentView, navigateToView]);

  useEffect(() => {
    pendingNextNavigation.current = null;
  }, [selectedNoteId]);

  const onPrevious = () => {
    if (!selectedEntity) return;
    const currentIndex = entities.findIndex(
      (entity) => entity.id === selectedEntity.id,
    );
    if (currentIndex > 0) {
      const previousEntity = entities[currentIndex - 1];
      navigateToView(hubViewForEntity(previousEntity), previousEntity.id);
    }
  };

  const onNext = async () => {
    if (!selectedEntity) return;
    const currentIndex = entities.findIndex(
      (entity) => entity.id === selectedEntity.id,
    );

    if (currentIndex === -1) {
      return;
    }

    if (currentIndex < entities.length - 1) {
      const nextEntity = entities[currentIndex + 1];
      navigateToView(hubViewForEntity(nextEntity), nextEntity.id);
      return;
    }

    if (hasNextPage && !isFetchingNextPage) {
      try {
        pendingNextNavigation.current = currentIndex + 1;
        await fetchNextPage();
      } catch (error) {
        console.error("Failed to fetch next page:", error);
        pendingNextNavigation.current = null;
      }
    }
  };

  const onNavigateToList = () => {
    pendingNextNavigation.current = null;
    navigateToView("list");
  };

  return { onPrevious, onNext, onNavigateToList };
}
